import type { ProfilePhotoConfig } from "./accountDataTypes.js";

export type { ProfilePhotoConfig };

export const PROFILE_PHOTO_ZOOM_MIN = 0.55;
export const PROFILE_PHOTO_ZOOM_MAX = 2.75;
export const PROFILE_PHOTO_MAX_ITEMS = 16;
export const PROFILE_PHOTO_BG_DEFAULT = "#0c101c";

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

function normalizeIdList(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    const out: string[] = [];
    for (const v of raw) {
      if (typeof v !== "string") continue;
      const id = v.trim();
      if (!id || out.includes(id)) continue;
      out.push(id);
      if (out.length >= PROFILE_PHOTO_MAX_ITEMS) break;
    }
    return out;
  }
  if (typeof raw === "string") {
    const id = raw.trim();
    return id ? [id] : [];
  }
  return [];
}

/** Accept #rgb / #rrggbb (case-insensitive); returns lowercase #rrggbb or null. */
export function normalizeProfilePhotoBackgroundColor(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const s = raw.trim();
  const m3 = /^#([0-9a-fA-F]{3})$/.exec(s);
  if (m3) {
    const [r, g, b] = m3[1].split("");
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  const m6 = /^#([0-9a-fA-F]{6})$/.exec(s);
  if (m6) return `#${m6[1]}`.toLowerCase();
  return null;
}

/** Normalize / clamp profile photo transform so cosmetics stay partially visible. */
export function normalizeProfilePhotoConfig(raw: unknown): ProfilePhotoConfig | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  let cosmeticIds = normalizeIdList(o.cosmeticIds);
  if (!cosmeticIds.length) {
    cosmeticIds = normalizeIdList(o.cosmeticId);
  }
  if (!cosmeticIds.length) return null;
  const zoom = clamp(Number(o.zoom), PROFILE_PHOTO_ZOOM_MIN, PROFILE_PHOTO_ZOOM_MAX);
  let rotation = Number(o.rotation);
  if (!Number.isFinite(rotation)) rotation = 0;
  rotation = ((rotation % 360) + 360) % 360;
  // Keep at least ~20% of the frame overlapping the art center band.
  const maxOffset = 0.72;
  const offsetX = clamp(Number(o.offsetX) || 0, -maxOffset, maxOffset);
  const offsetY = clamp(Number(o.offsetY) || 0, -maxOffset, maxOffset);
  const backgroundColor =
    normalizeProfilePhotoBackgroundColor(o.backgroundColor) || PROFILE_PHOTO_BG_DEFAULT;
  const backgroundFromBanner = o.backgroundFromBanner === true;
  return {
    cosmeticIds,
    /** Legacy single-id mirror for older readers. */
    cosmeticId: cosmeticIds[0],
    zoom: Number.isFinite(zoom) ? zoom : 1,
    rotation,
    offsetX,
    offsetY,
    backgroundColor,
    backgroundFromBanner,
  };
}

export function readProfilePhotoFromPayload(payload: {
  profile?: { photo?: unknown };
}): ProfilePhotoConfig | null {
  return normalizeProfilePhotoConfig(payload.profile?.photo ?? null);
}

export function profilePhotoCosmeticIds(
  photo: ProfilePhotoConfig | null | undefined,
): string[] {
  if (!photo) return [];
  if (Array.isArray(photo.cosmeticIds) && photo.cosmeticIds.length) {
    return photo.cosmeticIds.filter((id) => typeof id === "string" && id.length > 0);
  }
  if (typeof photo.cosmeticId === "string" && photo.cosmeticId) return [photo.cosmeticId];
  return [];
}

export function profilePhotoBackgroundColor(
  photo: ProfilePhotoConfig | null | undefined,
): string {
  return (
    normalizeProfilePhotoBackgroundColor(photo?.backgroundColor) || PROFILE_PHOTO_BG_DEFAULT
  );
}
